<script setup>
import {computed, ref, watch} from "vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import useActionSettings from "@/composables/useActionSettings";

const showModal = ref(false);
const minutesAssigned = ref(0);

const {minuteOptions, saveSettingsMutation, settingsQuery} = useActionSettings();
const { isLoading } = settingsQuery;
const { mutate: saveSettings, isPending: saving } = saveSettingsMutation;

const settings = computed(() => settingsQuery.data.value);

watch(
  settings,
  () => {
    showModal.value = !isLoading.value && !settings.value?.actions_accepted;
  },
  {immediate: true, deep: true}
);

const handleSave = () => {
  saveSettings({
    ...settings.value,
    minutes_assigned: minutesAssigned.value,
    initial: true,
  });
}

const handleSetupLater = () => {
  saveSettings({
    ...settings.value,
    minutes_assigned: 240, // default of 4 hours is set
    initial: true,
  });
}

const monthsToComplete = computed(() => {
  if (settings.value?.total_time > 0 && minutesAssigned.value > 0) {
    return Math.ceil(settings.value?.total_time / minutesAssigned.value);
  }
  return 0;
});

const closeModal = () => showModal.value = false;
</script>

<template>
  <Teleport to=".tailwind">
    <BaseModal
      v-if="showModal"
      modal-class="tw-w-[800px]"
      hide-close-button
      @close="closeModal"
    >
      <div class="tw-p-4">
        <h2 class="tw-text-2.5xl tw-font-bold tw-mb-4">
          Welcome To Our Action Engine!
        </h2>
        <p class="mb-4 tw-text-base">
          We've rolled out an exciting update which allows you to set the number of hours you want to spend on your
          SEO
          every month.
        </p>
        <p class="mb-4 tw-text-base">
          Simply choose the number of hours you want to commit to SEO every month, and we'll refresh your actions with
          the
          most important tasks.
          We'll even drop you an email when the next batch is ready.
        </p>
        <form
          class="tw-flex tw-flex-col tw-gap-6 tw-border tw-border-grey-mid tw-rounded-lg tw-p-6 tw-mb-6"
          @submit.prevent="handleSave"
        >
          <label
            class="tw-font-bold tw-text-grey-dark tw-text-xl"
            for="minutesAssigned"
          >
            Hours Per Month
          </label>

          <p class="tw-text-grey-dark tw-leading-snug tw-text-base">
            How many hours can you commit to your SEO per month?
          </p>

          <div class="tw-flex tw-align-middle tw-flex-col md:tw-flex-row tw-justify-between">
            <CustomMultiselect
              id="minutesAssigned"
              v-model="minutesAssigned"
              class="tw-text-left tw-h-12.5 tw-relative tw-w-[232px]"
              :close-on-select="true"
              :options="minuteOptions"
              :searchable="false"
              :disabled="saving"
              :can-deselect="false"
              :classes="{
                dropdown: 'tw-max-h-40 tw-absolute -tw-left-px -tw-right-px -tw-bottom-4 tw-transform tw-translate-y-full tw-border-2 tw-border-blue -tw-mt-px tw-overflow-y-scroll tw-z-50 tw-p-4 tw-bg-white tw-flex tw-flex-col tw-rounded-lg'
              }"
            >
              <template #icon>
                <svg-vue
                  icon="icons/clock"
                  class="tw-w-[18px] tw-h-[18px] tw-text-grey"
                />
              </template>
            </CustomMultiselect>

            <div class="tw-text-grey tw-text-base tw-w-[412px]">
              <p
                v-if="minutesAssigned"
                class="tw-text-inherit tw-leading-normal"
              >
                Based on this selection, it would take an estimated
                <span class="tw-font-bold">
                  {{ monthsToComplete }} months
                </span>
                to complete your website actions.
              </p>
              <p
                v-else
                class="tw-text-inherit tw-leading-normal"
              >
                Select an amount of hours to get an estimate for time until completion.
              </p>
            </div>
          </div>
        </form>

        <div class="tw-border tw-border-[#0ACCCC] tw-bg-[#D4FAED] tw-p-4 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-gap-1">
          <span class="tw-text-[#006666]">
            Don't worry you can change this at any time in
          </span>
          <a
            class="tw-text-blue-500 tw-flex tw-items-center tw-gap-1"
            href="/actions/settings"
          >
            <svg-vue
              icon="icons/settings"
              class="tw-w-4 tw-h-4 tw-block"
            />
            <span class="tw-block">Settings</span>
          </a>
        </div>

        <div class="tw-flex tw-gap-2 tw-justify-end tw-align-middle tw-w-full tw-mt-8">
          <DefaultButton
            variant="link"
            @click="() => handleSetupLater()"
          >
            Setup Later
          </DefaultButton>

          <DefaultButton
            :disabled="!minutesAssigned || saving"
            :loading="saving"
            @click="() => handleSave()"
          >
            Save
          </DefaultButton>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
